import { filterResourcesByBuilderId } from '@ctw/shared/api/common';
import { EncounterModel } from '@ctw/shared/api/fhir/models/encounter';
import {
  SYSTEM_SUMMARY,
  SYSTEM_ZUS_OWNER,
  SYSTEM_ZUS_SUMMARY,
  SYSTEM_ZUS_THIRD_PARTY,
} from '@ctw/shared/api/fhir/system-urls';
import { fqsRequestAll, MAX_OBJECTS_PER_REQUEST } from '@ctw/shared/api/fqs/client';
import {
  type EncounterGraphqlResponse,
  encountersQuery,
} from '@ctw/shared/api/fqs/queries/encounters';
import { getLensBuilderId } from '@ctw/shared/api/urls';
import { dedupeAndMergeEncounters } from '@ctw/shared/content/encounters/helpers/filters';
import { FQS_HOTSTORAGE_REQUEST_OPTIONS } from '@ctw/shared/utils/hotstorage-header';
import { useEffect, useState } from 'react';
import { getLensSummariesWithSyncedWithRecord } from '../api/fhir/summary';
import { usePatientQuery } from '../context/patient-provider';
import {
  QUERY_KEY_PATIENT_BUILDER_ENCOUNTERS,
  QUERY_KEY_PATIENT_SUMMARY_ENCOUNTERS,
} from '../utils/query-keys';

export function usePatientEncounters(limit = MAX_OBJECTS_PER_REQUEST, enabled = true) {
  const queryForSummary = usePatientQuery({
    queryId: QUERY_KEY_PATIENT_SUMMARY_ENCOUNTERS,
    queryKey: [limit],
    queryFn: async ({ requestContext, telemetry, graphqlClient, patient }) => {
      const { data } = await fqsRequestAll<EncounterGraphqlResponse>(
        telemetry,
        graphqlClient,
        encountersQuery,
        'EncounterConnection',
        {
          upid: patient.UPID,
          cursor: '',
          first: limit,
          filter: {
            tag: {
              allmatch: [
                `${SYSTEM_ZUS_SUMMARY}|Common`,
                `${SYSTEM_ZUS_OWNER}|builder/${getLensBuilderId(requestContext.env)}`,
              ],
            },
          },
        },
        undefined,
        FQS_HOTSTORAGE_REQUEST_OPTIONS,
      );
      const nodes = data.EncounterConnection.edges.map((x) => x.node);
      const results = nodes.map(
        (c) => new EncounterModel(c, c.ProvenanceList, undefined, c.BasicList),
      );
      return dedupeAndMergeEncounters(results, telemetry, 'usePatientEncounters:summary');
    },
    enabled,
  });
  const queryForBuilder = usePatientQuery({
    queryId: QUERY_KEY_PATIENT_BUILDER_ENCOUNTERS,
    queryKey: [limit],
    queryFn: async ({ requestContext, telemetry, graphqlClient, patient }) => {
      const { data } = await fqsRequestAll<EncounterGraphqlResponse>(
        telemetry,
        graphqlClient,
        encountersQuery,
        'EncounterConnection',
        {
          upid: patient.UPID,
          cursor: '',
          first: limit,
          filter: {
            tag: {
              nonematch: [SYSTEM_SUMMARY, SYSTEM_ZUS_THIRD_PARTY],
              // TODO: There's a bug in FQS that doesn't allow filtering with nonematch AND allmatch.
              // Uncomment the line below once https://zeushealth.atlassian.net/browse/DRT-249 is resolved.
              // allmatch: [`${SYSTEM_ZUS_OWNER}|builder/${requestContext.builderId}`],
            },
          },
        },
        undefined,
        FQS_HOTSTORAGE_REQUEST_OPTIONS,
      );
      let nodes = data.EncounterConnection.edges.map((x) => x.node);
      nodes = filterResourcesByBuilderId(nodes, requestContext.builderId);
      const results = nodes.map(
        (c) => new EncounterModel(c, c.ProvenanceList, undefined, c.BasicList),
      );
      return dedupeAndMergeEncounters(results, telemetry, 'usePatientEncounters:builder');
    },
    enabled,
  });
  const [encounters, setEncounters] = useState<Array<EncounterModel>>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  useEffect(() => {
    setEncounters(
      getLensSummariesWithSyncedWithRecord(queryForSummary.data ?? [], queryForBuilder.data ?? []),
    );
  }, [
    queryForSummary.data,
    queryForBuilder.data,
    queryForSummary.isLoading,
    queryForBuilder.isLoading,
  ]);

  return {
    ...queryForSummary,
    isLoading: queryForSummary.isLoading || queryForBuilder.isLoading,
    data: encounters,
  };
}
